import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, API } from "../App";
import {
  ArrowLeft, MessageSquareText, Megaphone, Sparkles, Loader2, Save, RotateCcw,
  Trash2, Send, BellOff, CheckCircle2, AlertTriangle, Pencil
} from "lucide-react";

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` }, withCredentials: true });

// ---------- Tab 1: Plantillas por evento ----------
const TemplateCard = ({ tpl, token, onSaved }) => {
  const [template, setTemplate] = useState(tpl.template);
  const [channels, setChannels] = useState(tpl.channels);
  const [enabled, setEnabled] = useState(tpl.enabled);
  const [config, setConfig] = useState(tpl.config || {});
  const [saving, setSaving] = useState(false);

  const dirty =
    template !== tpl.template ||
    enabled !== tpl.enabled ||
    JSON.stringify(channels) !== JSON.stringify(tpl.channels) ||
    JSON.stringify(config) !== JSON.stringify(tpl.config || {});

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/message-templates/${tpl.event_id}`, {
        template, channels, enabled, config
      }, authHeaders(token));
      toast.success(`Plantilla "${tpl.label}" guardada`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      const res = await axios.post(`${API}/admin/message-templates/${tpl.event_id}/reset`, {}, authHeaders(token));
      setTemplate(res.data.template);
      setChannels(res.data.channels);
      setEnabled(res.data.enabled);
      setConfig(res.data.config || {});
      toast.success("Plantilla restaurada al valor original");
      onSaved();
    } catch {
      toast.error("Error al restaurar");
    }
  };

  const isSilent = !channels.whatsapp;

  return (
    <div className={`bg-white rounded-2xl border p-4 space-y-3 ${enabled ? "border-[#E2E8F0]" : "border-dashed border-[#CBD5E1] opacity-70"}`} data-testid={`template-card-${tpl.event_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-[#0F172A] text-sm">{tpl.label}</h3>
            {isSilent && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 flex items-center gap-0.5">
                <BellOff size={9} /> SILENCIOSA
              </span>
            )}
            {tpl.is_customized && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">PERSONALIZADA</span>
            )}
          </div>
          <p className="text-xs text-[#64748B] mt-0.5">{tpl.description}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[10px] text-[#64748B]">Activo</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`template-enabled-${tpl.event_id}`} />
        </div>
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-1.5 text-xs text-[#374151]">
          <Switch checked={!!channels.whatsapp} onCheckedChange={(v) => setChannels({ ...channels, whatsapp: v })} data-testid={`channel-wa-${tpl.event_id}`} />
          WhatsApp
        </label>
        <label className="flex items-center gap-1.5 text-xs text-[#374151]">
          <Switch checked={!!channels.web} onCheckedChange={(v) => setChannels({ ...channels, web: v })} data-testid={`channel-web-${tpl.event_id}`} />
          Notificación web
        </label>
      </div>

      <Textarea
        value={template}
        onChange={(e) => setTemplate(e.target.value)}
        rows={3}
        className="text-sm rounded-xl border-[#E2E8F0]"
        data-testid={`template-text-${tpl.event_id}`}
      />
      <p className="text-[10px] text-[#94A3B8]">
        Variables: {(tpl.variables || []).map((v) => `{${v}}`).join(", ")}
      </p>

      {tpl.event_id === "auction_ending" && (
        <div className="flex items-center gap-2">
          <Label className="text-xs">Avisar cuando falten</Label>
          <Input
            type="number" min="1" max="72"
            value={config.hours_before ?? 6}
            onChange={(e) => setConfig({ ...config, hours_before: parseFloat(e.target.value) || 6 })}
            className="w-20 h-8 text-xs rounded-lg"
            data-testid="config-hours-before"
          />
          <span className="text-xs text-[#64748B]">horas para el cierre</span>
        </div>
      )}
      {tpl.event_id === "cart_reminder" && (
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-xs">Recordar después de</Label>
          <Input
            type="number" min="1" max="168"
            value={config.hours_after ?? 24}
            onChange={(e) => setConfig({ ...config, hours_after: parseFloat(e.target.value) || 24 })}
            className="w-20 h-8 text-xs rounded-lg"
            data-testid="config-hours-after"
          />
          <span className="text-xs text-[#64748B]">horas · Descuento</span>
          <Input
            type="number" min="0" max="90"
            value={config.discount_percent ?? 0}
            onChange={(e) => setConfig({ ...config, discount_percent: parseFloat(e.target.value) || 0 })}
            className="w-20 h-8 text-xs rounded-lg"
            data-testid="config-discount-percent"
          />
          <span className="text-xs text-[#64748B]">% (0 = sin cupón)</span>
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={!dirty || saving} className="rounded-full bg-[#475569] hover:bg-[#334155] text-xs" data-testid={`template-save-${tpl.event_id}`}>
          {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : <Save size={13} className="mr-1" />}
          Guardar
        </Button>
        {tpl.is_customized && (
          <Button size="sm" variant="outline" onClick={reset} className="rounded-full text-xs" data-testid={`template-reset-${tpl.event_id}`}>
            <RotateCcw size={13} className="mr-1" /> Restaurar
          </Button>
        )}
      </div>
    </div>
  );
};

const TemplatesTab = ({ token }) => {
  const [templates, setTemplates] = useState([]);
  const [twilioOk, setTwilioOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testNumber, setTestNumber] = useState("");
  const [testing, setTesting] = useState(false);

  const testWhatsApp = async () => {
    if (!testNumber.trim()) {
      toast.error("Escribe un número de WhatsApp (8 dígitos o formato +507...)");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/test-whatsapp`, { to_number: testNumber }, authHeaders(token));
      if (res.data.success) {
        toast.success(`Mensaje de prueba enviado a ${res.data.to} (SID: ${res.data.sid})`);
      } else {
        toast.error(res.data.error || "Error al enviar prueba");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar prueba");
    } finally {
      setTesting(false);
    }
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/message-templates`, authHeaders(token));
      setTemplates(res.data.templates);
      setTwilioOk(res.data.twilio_configured);
    } catch {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#64748B]" /></div>;

  return (
    <div className="space-y-3">
      {!twilioOk && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-xs text-amber-800" data-testid="twilio-warning">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <span>Twilio no está configurado: los mensajes de WhatsApp se registran en modo simulado. Las notificaciones web sí se envían de verdad.</span>
        </div>
      )}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 flex items-center gap-2">
        <Input
          placeholder="Nº WhatsApp de prueba (ej. 61234567)"
          value={testNumber}
          onChange={(e) => setTestNumber(e.target.value)}
          className="h-9 rounded-lg text-xs flex-1"
          data-testid="test-whatsapp-input"
        />
        <Button size="sm" onClick={testWhatsApp} disabled={testing} className="rounded-full text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="test-whatsapp-btn">
          {testing ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />}
          Probar WhatsApp
        </Button>
      </div>
      <p className="text-xs text-[#64748B]">
        Personaliza el mensaje de cada suceso y elige el canal. <strong>Silenciosa</strong> = solo notificación en el sitio web (sin WhatsApp), para no saturar al cliente.
      </p>
      {templates.map((tpl) => (
        <TemplateCard key={`${tpl.event_id}-${tpl.is_customized}-${tpl.template}`} tpl={tpl} token={token} onSaved={load} />
      ))}
    </div>
  );
};

// ---------- Tab 2: Pop-up promocional ----------
const EMPTY_POPUP_FORM = { title: "", message: "", image_url: "", product_id: "", discount_code: "", discount_percent: "", button_text: "", action_url: "", starts_at: "", ends_at: "", active: true, notify_clients: true, popup_type: "modal", background_color: "#FFFFFF", use_marquee: false };

const POPUP_TYPES = [
  { id: "modal", label: "Modal grande", hint: "Centrado, con imagen de producto" },
  { id: "corner", label: "Esquina pequeña", hint: "Tarjeta en la esquina inferior derecha" },
  { id: "bar", label: "Barra completa abajo", hint: "Franja angosta en todo el ancho inferior" },
  { id: "header", label: "Encabezado arriba", hint: "Franja angosta en todo el ancho superior" },
];

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DESTINATIONS = [
  { id: "product", label: "Producto seleccionado" },
  { id: "/", label: "Inicio" },
  { id: "/section/auctions", label: "Subastas" },
  { id: "/section/direct", label: "Venta directa" },
  { id: "/search", label: "Búsqueda" },
  { id: "custom", label: "URL personalizada" },
];

const PopupsTab = ({ token }) => {
  const [popups, setPopups] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [destination, setDestination] = useState("product");
  const [customUrl, setCustomUrl] = useState("");
  const [form, setForm] = useState(EMPTY_POPUP_FORM);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/popups`, authHeaders(token));
      setPopups(res.data.popups);
    } catch {
      toast.error("Error al cargar pop-ups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/products?limit=100`).then((res) => setProducts(res.data || [])).catch(() => {});
  }, []);

  const handleProductSelect = (productId) => {
    if (!productId) {
      setForm({ ...form, product_id: "" });
      return;
    }
    const p = products.find((x) => x.product_id === productId);
    if (!p) return;
    const price = p.buy_now_price || p.price;
    setForm({
      ...form,
      product_id: p.product_id,
      title: p.title,
      message: p.description ? p.description.slice(0, 140) : `¡${p.title} por $${price}! No te lo pierdas.`,
      image_url: p.image_url || "",
      discount_code: p.discount_code || form.discount_code,
    });
    if (destination === "custom") setDestination("product");
    toast.success("Campos completados con la información del producto");
  };

  const resolveActionUrl = () => {
    if (destination === "product") return "";
    if (destination === "custom") return customUrl.trim();
    return destination;
  };

  const startEdit = (p) => {
    setEditingId(p.popup_id);
    setForm({
      title: p.title || "",
      message: p.message || "",
      image_url: p.image_url || "",
      product_id: p.product_id || "",
      discount_code: p.discount_code || "",
      discount_percent: p.discount_percent ? String(p.discount_percent) : "",
      button_text: p.button_text || "",
      action_url: p.action_url || "",
      starts_at: isoToLocal(p.starts_at),
      ends_at: isoToLocal(p.ends_at),
      active: !!p.active,
      notify_clients: false,
      popup_type: p.popup_type || "modal",
      background_color: p.background_color || "#FFFFFF",
      use_marquee: !!p.use_marquee,
    });
    if (p.action_url) {
      const known = DESTINATIONS.find((d) => d.id === p.action_url);
      if (known) { setDestination(p.action_url); setCustomUrl(""); }
      else { setDestination("custom"); setCustomUrl(p.action_url); }
    } else {
      setDestination("product");
      setCustomUrl("");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_POPUP_FORM);
    setDestination("product");
    setCustomUrl("");
  };

  const save = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Título y mensaje son obligatorios");
      return;
    }
    if (destination === "custom" && !customUrl.trim()) {
      toast.error("Escribe la URL personalizada del botón");
      return;
    }
    if (form.starts_at && form.ends_at && form.ends_at <= form.starts_at) {
      toast.error("La fecha de fin debe ser posterior a la de inicio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_percent: form.discount_percent ? parseFloat(form.discount_percent) : 0,
        action_url: resolveActionUrl(),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : "",
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : "",
      };
      if (editingId) {
        delete payload.notify_clients;
        await axios.put(`${API}/admin/popups/${editingId}`, payload, authHeaders(token));
        toast.success("Pop-up actualizado");
      } else {
        ["image_url", "product_id", "discount_code", "button_text", "action_url", "starts_at", "ends_at"].forEach((k) => { if (!payload[k]) delete payload[k]; });
        const res = await axios.post(`${API}/admin/popups`, payload, authHeaders(token));
        if (res.data.scheduled) {
          toast.success("Pop-up programado: se activará automáticamente en la fecha indicada");
        } else {
          toast.success(`Pop-up creado${res.data.notified ? ` · ${res.data.notified} clientes avisados` : ""}`);
        }
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar pop-up");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (popup) => {
    try {
      const res = await axios.put(`${API}/admin/popups/${popup.popup_id}`, { active: !popup.active }, authHeaders(token));
      toast.success(popup.active ? "Pop-up desactivado" : `Pop-up activado${res.data.notified ? ` · ${res.data.notified} clientes avisados` : ""}`);
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const remove = async (popup) => {
    try {
      await axios.delete(`${API}/admin/popups/${popup.popup_id}`, authHeaders(token));
      toast.success("Pop-up eliminado");
      if (editingId === popup.popup_id) cancelEdit();
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const selectedProduct = products.find((p) => p.product_id === form.product_id);

  return (
    <div className="space-y-4">
      <div className={`bg-white rounded-2xl border p-4 space-y-3 ${editingId ? "border-indigo-300" : "border-[#E2E8F0]"}`}>
        <h3 className="font-bold text-sm text-[#0F172A] flex items-center gap-2">
          <Sparkles size={15} /> {editingId ? "Editar pop-up" : "Nuevo pop-up promocional"}
        </h3>

        {/* Popup style/type selector */}
        <div className="space-y-1.5">
          <Label className="text-xs">Estilo del pop-up</Label>
          <div className="grid grid-cols-2 gap-2" data-testid="popup-type-selector">
            {POPUP_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm({ ...form, popup_type: t.id })}
                className={`text-left px-2.5 py-2 rounded-xl border text-xs transition-colors ${
                  form.popup_type === t.id
                    ? "bg-[#475569] text-white border-[#475569]"
                    : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#475569]"
                }`}
                data-testid={`popup-type-${t.id}`}
              >
                <span className="font-semibold block">{t.label}</span>
                <span className={`block text-[10px] mt-0.5 ${form.popup_type === t.id ? "text-white/80" : "text-[#94A3B8]"}`}>{t.hint}</span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#94A3B8]">
            Puede haber 1 "Modal grande" y 1 pop-up pequeño ("Esquina", "Barra" o "Encabezado") activos al mismo tiempo.
          </p>
        </div>

        {form.popup_type !== "modal" && (
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-2.5" data-testid="popup-bg-color-row">
            <Label className="text-xs flex-shrink-0 mb-2 block">Modo de color</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, background_color: "#FFFFFF" })}
                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-colors ${
                  form.background_color === "#FFFFFF" ? "border-[#475569] bg-white" : "border-[#E2E8F0] bg-white"
                }`}
                data-testid="popup-bg-white"
              >
                <span className="w-5 h-5 rounded-full bg-white border border-[#CBD5E1]" />
                <span className="text-xs font-semibold text-[#0F172A]">Blanco (claro)</span>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, background_color: "#000000" })}
                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-colors ${
                  form.background_color === "#000000" ? "border-[#475569] bg-[#0F172A]" : "border-[#E2E8F0] bg-[#0F172A]"
                }`}
                data-testid="popup-bg-black"
              >
                <span className="w-5 h-5 rounded-full bg-black border border-[#475569]" />
                <span className="text-xs font-semibold text-white">Negro (oscuro)</span>
              </button>
            </div>
            <p className="text-[10px] text-[#94A3B8] mt-1.5">El texto se ajusta automáticamente: negro sobre blanco, blanco sobre negro.</p>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E2E8F0]">
              <div>
                <Label className="text-xs">Animación de movimiento</Label>
                <p className="text-[10px] text-[#94A3B8]">El mismo efecto sutil del encabezado superior, para llamar más la atención</p>
              </div>
              <Switch checked={form.use_marquee} onCheckedChange={(v) => setForm({ ...form, use_marquee: v })} data-testid="popup-marquee-switch" />
            </div>
          </div>
        )}

        {/* Product picker - autofills fields */}
        <div className="space-y-1.5">
          <Label className="text-xs">Usar un producto existente (autocompleta foto e información)</Label>
          <select
            value={form.product_id}
            onChange={(e) => handleProductSelect(e.target.value)}
            className="w-full h-10 rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A]"
            data-testid="popup-product-select"
          >
            <option value="">— Sin producto (pop-up libre) —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>
                {p.title} · ${p.buy_now_price || p.price} {p.sale_type === "auction" ? "(Subasta)" : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedProduct && (
          <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl p-2 border border-[#E2E8F0]">
            <img src={selectedProduct.image_url} alt={selectedProduct.title} className="w-10 h-10 rounded-lg object-cover" />
            <p className="text-xs text-[#64748B] flex-1 truncate">Foto e info tomadas de <strong>{selectedProduct.title}</strong>. Puedes editar los campos.</p>
          </div>
        )}

        <Input placeholder="Título (ej: ¡Mega descuento en iPhone!)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid="popup-title-input" />
        <Textarea placeholder="Mensaje de la promoción..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className="rounded-xl" data-testid="popup-message-input" />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="URL de imagen (opcional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-xl text-xs" data-testid="popup-image-input" />
          <Input placeholder="Código de descuento (opcional)" value={form.discount_code} onChange={(e) => setForm({ ...form, discount_code: e.target.value })} className="rounded-xl text-xs" data-testid="popup-code-input" />
        </div>

        {form.product_id && (
          <div className="flex items-center gap-3 bg-[#FFF7ED] border border-orange-200 rounded-xl p-2.5" data-testid="popup-discount-row">
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Input
                type="number"
                min="0"
                max="90"
                placeholder="0"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                className="w-20 rounded-xl text-sm"
                data-testid="popup-discount-input"
              />
              <span className="text-sm font-bold text-[#9A3412]">%</span>
            </div>
            <p className="text-xs text-[#9A3412] leading-tight">
              Descuento automático sobre el precio del producto mientras el pop-up esté activo (solo venta directa, no subastas). Se muestra en la página del producto y se quita solo al terminar.
            </p>
          </div>
        )}

        {/* Action button destination */}
        <div className="space-y-1.5">
          <Label className="text-xs">Botón de acción: ¿a dónde lleva al cliente?</Label>
          <div className="flex flex-wrap gap-2" data-testid="popup-destination-selector">
            {DESTINATIONS.map((d) => {
              const disabled = d.id === "product" && !form.product_id;
              return (
                <button
                  key={d.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => setDestination(d.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    destination === d.id
                      ? "bg-[#475569] text-white border-[#475569]"
                      : disabled
                        ? "bg-[#F1F5F9] text-[#CBD5E1] border-[#E2E8F0] cursor-not-allowed"
                        : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#475569]"
                  }`}
                  data-testid={`popup-dest-${d.id === "/" ? "home" : d.id.replace(/\//g, "-").replace(/^-/, "") || d.id}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          {destination === "custom" && (
            <Input
              placeholder="URL o ruta (ej: /my-purchases o https://...)"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              className="rounded-xl text-xs"
              data-testid="popup-custom-url-input"
            />
          )}
          <Input placeholder="Texto del botón (opcional, ej: ¡Lo quiero!)" value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} className="rounded-xl text-xs" data-testid="popup-button-input" />
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Inicio programado (opcional)</Label>
            <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="rounded-xl text-xs" data-testid="popup-starts-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Fin programado (opcional)</Label>
            <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="rounded-xl text-xs" data-testid="popup-ends-input" />
          </div>
        </div>
        <p className="text-[10px] text-[#94A3B8]">Con fechas programadas el pop-up se muestra y se oculta solo. Sin fechas, se controla con el switch "Activar".</p>

        <div className="flex items-center gap-5">
          <label className="flex items-center gap-1.5 text-xs text-[#374151]">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="popup-active-switch" />
            Activar ahora
          </label>
          {!editingId && (
            <label className="flex items-center gap-1.5 text-xs text-[#374151]">
              <Switch checked={form.notify_clients} onCheckedChange={(v) => setForm({ ...form, notify_clients: v })} data-testid="popup-notify-switch" />
              Avisar a clientes (según plantilla "Promociones")
            </label>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#475569] hover:bg-[#334155]" data-testid="popup-create-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
            {editingId ? "Guardar cambios" : "Crear pop-up"}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={cancelEdit} className="rounded-full" data-testid="popup-cancel-edit-btn">
              Cancelar
            </Button>
          )}
        </div>
        <p className="text-[10px] text-[#94A3B8]">Puede haber 1 modal + 1 pop-up pequeño (esquina o barra) activos a la vez. Se muestra una vez por visitante.</p>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-[#64748B]" /></div>
      ) : popups.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-4">No has creado pop-ups todavía</p>
      ) : (
        <div className="space-y-2" data-testid="popup-list">
          {popups.map((p) => (
            <div key={p.popup_id} className={`bg-white rounded-xl border p-3 flex items-center gap-3 ${p.active ? "border-emerald-300" : "border-[#E2E8F0]"}`}>
              {p.image_url && <img src={p.image_url} alt={p.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm text-[#0F172A] truncate">{p.title}</p>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 flex-shrink-0">
                    {POPUP_TYPES.find((t) => t.id === (p.popup_type || "modal"))?.label || "Modal"}
                  </span>
                  {p.active && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-0.5"><CheckCircle2 size={9} /> ACTIVO</span>}
                </div>
                <p className="text-xs text-[#64748B] truncate">{p.message}</p>
                <div className="flex items-center gap-2">
                  {p.discount_code && <p className="text-[10px] text-[#F97316] font-mono">Cupón: {p.discount_code}</p>}
                  {p.discount_percent > 0 && <p className="text-[10px] font-bold text-[#EA580C]">Descuento automático: -{p.discount_percent}%</p>}
                  {(p.action_url || p.product_id) && (
                    <p className="text-[10px] text-[#64748B]">→ {p.action_url || `/product/${p.product_id}`}</p>
                  )}
                </div>
                {(p.starts_at || p.ends_at) && (
                  <p className="text-[10px] text-indigo-600">
                    ⏱ {p.starts_at ? `Desde ${new Date(p.starts_at).toLocaleString("es-PA", { dateStyle: "short", timeStyle: "short" })}` : ""}
                    {p.ends_at ? ` · Hasta ${new Date(p.ends_at).toLocaleString("es-PA", { dateStyle: "short", timeStyle: "short" })}` : ""}
                  </p>
                )}
              </div>
              <button onClick={() => startEdit(p)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg" data-testid={`popup-edit-${p.popup_id}`}>
                <Pencil size={14} />
              </button>
              <Button size="sm" variant="outline" onClick={() => toggleActive(p)} className="rounded-full text-xs" data-testid={`popup-toggle-${p.popup_id}`}>
                {p.active ? "Desactivar" : "Activar"}
              </Button>
              <button onClick={() => remove(p)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`popup-delete-${p.popup_id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Tab 3: Difusión masiva ----------
const AUDIENCES = [
  { id: "all", label: "Todos los clientes" },
  { id: "recent_buyers", label: "Compradores recientes (30 días)" },
  { id: "never_bought", label: "Nunca han comprado" },
];

const BroadcastTab = ({ token }) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState("web");
  const [audience, setAudience] = useState("all");
  const [audienceCount, setAudienceCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setAudienceCount(null);
    axios.get(`${API}/admin/broadcast/audience-count?audience=${audience}`, authHeaders(token))
      .then((r) => setAudienceCount(r.data.count))
      .catch(() => setAudienceCount(null));
  }, [audience]);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/broadcasts`, authHeaders(token));
      setHistory(res.data.broadcasts);
    } catch { /* noop */ }
  };

  useEffect(() => { loadHistory(); }, []);

  const send = async () => {
    if (!message.trim()) {
      toast.error("Escribe un mensaje");
      return;
    }
    const audienceLabel = AUDIENCES.find((a) => a.id === audience)?.label || "Todos";
    if (!window.confirm(`¿Enviar este mensaje a "${audienceLabel}"${audienceCount !== null ? ` (${audienceCount} clientes)` : ""} por ${channel === "both" ? "WhatsApp y web" : channel === "whatsapp" ? "WhatsApp" : "notificación web"}?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcast`, { title: title || null, message, channel, audience }, authHeaders(token));
      toast.success(`Enviado: ${res.data.web_sent} web · ${res.data.whatsapp_sent} WhatsApp${!res.data.twilio_configured && res.data.whatsapp_sent > 0 ? " (simulado)" : ""}`);
      setTitle(""); setMessage("");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar difusión");
    } finally {
      setSending(false);
    }
  };

  const channelOptions = [
    { id: "web", label: "Notificación web" },
    { id: "whatsapp", label: "WhatsApp" },
    { id: "both", label: "Ambos" },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 space-y-3">
        <h3 className="font-bold text-sm text-[#0F172A] flex items-center gap-2"><Megaphone size={15} /> Mensaje masivo a clientes</h3>
        <Input placeholder="Título (opcional)" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl" data-testid="broadcast-title-input" />
        <Textarea placeholder="Escribe el mensaje... Puedes usar {usuario} para personalizar con el nombre del cliente." value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="rounded-xl" data-testid="broadcast-message-input" />
        <div className="space-y-1.5">
          <Label className="text-xs">¿A quién se lo envías?</Label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setAudience(a.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${audience === a.id ? "bg-[#475569] text-white border-[#475569]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`}
                data-testid={`broadcast-audience-${a.id}`}
              >
                {a.label}
              </button>
            ))}
          </div>
          {audienceCount !== null && (
            <p className="text-[11px] text-emerald-700" data-testid="audience-count">
              📬 Llegará a <strong>{audienceCount}</strong> cliente{audienceCount !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Canal de envío</Label>
          <div className="flex gap-2">
            {channelOptions.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setChannel(c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${channel === c.id ? "bg-[#475569] text-white border-[#475569]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`}
                data-testid={`broadcast-channel-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={send} disabled={sending} className="rounded-full bg-[#475569] hover:bg-[#334155]" data-testid="broadcast-send-btn">
          {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
          Enviar a todos los clientes
        </Button>
      </div>

      {history.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-[#64748B]">Últimas difusiones</h4>
          {history.map((b) => (
            <div key={b.broadcast_id} className="bg-white rounded-xl border border-[#E2E8F0] p-3">
              <p className="text-sm text-[#0F172A]">{b.title ? <strong>{b.title}: </strong> : null}{b.message}</p>
              <p className="text-[10px] text-[#94A3B8] mt-1">
                {new Date(b.created_at).toLocaleString("es-PA")} · {b.channel} · {AUDIENCES.find((a) => a.id === b.audience)?.label || "Todos"} · {b.web_sent} web / {b.whatsapp_sent} WhatsApp
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Page ----------
export default function AdminMessagingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  if (user && !user.is_admin) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />
      <main className="max-w-[640px] mx-auto px-4 py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-4" data-testid="back-button">
          <ArrowLeft size={20} /> Volver
        </button>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <MessageSquareText size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#0F172A]">Mensajes y Promociones</h1>
            <p className="text-xs text-[#64748B]">Personaliza los mensajes automáticos, pop-ups y difusiones</p>
          </div>
        </div>

        <Tabs defaultValue="templates">
          <TabsList className="grid grid-cols-3 w-full rounded-full mb-4">
            <TabsTrigger value="templates" className="rounded-full text-xs" data-testid="tab-templates">Plantillas</TabsTrigger>
            <TabsTrigger value="popups" className="rounded-full text-xs" data-testid="tab-popups">Pop-ups</TabsTrigger>
            <TabsTrigger value="broadcast" className="rounded-full text-xs" data-testid="tab-broadcast">Difusión</TabsTrigger>
          </TabsList>
          <TabsContent value="templates">{token && <TemplatesTab token={token} />}</TabsContent>
          <TabsContent value="popups">{token && <PopupsTab token={token} />}</TabsContent>
          <TabsContent value="broadcast">{token && <BroadcastTab token={token} />}</TabsContent>
        </Tabs>
      </main>
      <BottomNav />
    </div>
  );
}
